import { useState } from "react";
import { format } from "date-fns";
import { ChevronDown, ExternalLink, MessageCircle, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { openWhatsappChat } from "@/features/razorpay/utils/paymentLinkShare";
import { CopyButton } from "../ToolBits";
import type { ToolItemView, ToolLinkItem, ToolRecordView, ToolScoreItem, ToolSectionView } from "../../types";

interface LeadContact {
  name?: string | null;
  phone?: string | null;
  phoneCountryCode?: string | null;
}

/** http(s) only. The server already checks; a link is never trusted twice over. */
const isSafeUrl = (value: string) => /^https?:\/\//i.test(value);

const whatsappDigits = (lead: LeadContact) =>
  `${lead.phoneCountryCode ?? ""}${lead.phone ?? ""}`.replace(/\D/g, "");

function fillMessage(template: string | null | undefined, url: string, lead: LeadContact) {
  const message = template || `Hi {{lead.name}}, ${"{url}"}`;
  return message
    .split("{{lead.name}}")
    .join(lead.name ?? "")
    .split("{url}")
    .join(url);
}

function formatValue(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : format(date, "d MMM yyyy, h:mm a");
}

function LinkItem({ item, lead }: { item: ToolLinkItem; lead: LeadContact }) {
  if (!isSafeUrl(item.value)) return null;
  const digits = whatsappDigits(lead);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#e6e8e7] bg-white px-3 py-2">
      <span className="min-w-0 flex-1 text-[13px] text-[#051912]">{item.label ?? "Link"}</span>
      <a
        href={item.value}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex h-7 items-center gap-1 rounded-md border border-[#e6e8e7] bg-white px-2 text-[12px] text-[#385047] hover:bg-[#f8fbf9]"
      >
        <ExternalLink className="size-3.5" />
        Open
      </a>
      <CopyButton value={item.value} />
      {item.share && digits ? (
        <button
          type="button"
          onClick={() => openWhatsappChat(digits, fillMessage(item.shareMessage, item.value, lead))}
          className="inline-flex h-7 items-center gap-1 rounded-md bg-[#156548] px-2 text-[12px] text-white hover:opacity-90"
        >
          <MessageCircle className="size-3.5" />
          WhatsApp
        </button>
      ) : null}
    </div>
  );
}

function ScoreItem({ item }: { item: ToolScoreItem }) {
  const max = item.max ?? 100;
  const share = Math.max(0, Math.min(100, (item.value / max) * 100));
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2 text-[12px]">
        <span className="text-[#5b6b64]">{item.label}</span>
        <span className="font-medium tabular-nums text-[#051912]">
          {item.value}
          <span className="text-[#8a9a93]"> / {max}</span>
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-[#eef1f0]">
        <div className="h-full rounded-full bg-[#156548]" style={{ width: `${share}%` }} />
      </div>
    </div>
  );
}

function Labelled({ label, children }: { label: string | null; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      {label ? <span className="text-[12px] text-[#5b6b64]">{label}</span> : null}
      {children}
    </div>
  );
}

function ItemView({ item, lead }: { item: ToolItemView; lead: LeadContact }) {
  switch (item.type) {
    case "number":
    case "score":
      return item.type === "score" && item.max !== 0 ? (
        <ScoreItem item={item} />
      ) : (
        <Labelled label={item.label}>
          <span className="text-[13px] tabular-nums text-[#051912]">{item.value}</span>
        </Labelled>
      );
    case "link":
      return <LinkItem item={item} lead={lead} />;
    case "audio":
      if (!isSafeUrl(item.value)) return null;
      return (
        <Labelled label={item.label}>
          <audio controls preload="none" src={item.value} className="h-9 w-full" />
        </Labelled>
      );
    case "list":
      return (
        <Labelled label={item.label}>
          <ul className="ml-4 list-disc space-y-0.5 text-[13px] leading-snug text-[#051912]">
            {item.value.map((row, i) => (
              <li key={i}>{String(row)}</li>
            ))}
          </ul>
        </Labelled>
      );
    case "table":
      return (
        <Labelled label={item.label}>
          <div className="overflow-x-auto rounded-lg border border-[#e6e8e7]">
            <table className="w-full text-left text-[12px]">
              <thead className="bg-[#f8fbf9] text-[#5b6b64]">
                <tr>
                  {item.columns.map((c) => (
                    <th key={c.label} className="px-3 py-2 font-medium">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {item.value.map((row, r) => (
                  <tr key={r} className="border-t border-[#f0f2f1] align-top">
                    {row.map((cell, c) => (
                      <td key={c} className="px-3 py-2 text-[#051912]">
                        {item.columns[c]?.type === "boolean" ? (cell ? "Yes" : "No") : String(cell ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Labelled>
      );
    case "longtext":
      return (
        <Labelled label={item.label}>
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-[#051912]">{item.value}</p>
        </Labelled>
      );
    case "badge":
      return (
        <Labelled label={item.label}>
          <span className="inline-flex w-fit rounded-md bg-[#e3f4ec] px-2 py-0.5 text-[13px] font-medium text-[#156548]">
            {item.value}
          </span>
        </Labelled>
      );
    case "date":
      return (
        <Labelled label={item.label}>
          <span className="text-[13px] text-[#051912]">{formatValue(item.value)}</span>
        </Labelled>
      );
    default:
      return (
        <Labelled label={item.label}>
          <span className="text-[13px] text-[#051912]">{String(item.value)}</span>
        </Labelled>
      );
  }
}

function Section({ section, lead }: { section: ToolSectionView; lead: LeadContact }) {
  const [open, setOpen] = useState(!section.collapsed);
  const compact = section.items.filter((i) => i.compact);
  const rest = section.items.filter((i) => !i.compact);

  return (
    <section
      className={cn(
        "rounded-xl border bg-white",
        section.internal ? "border-[#f3d9c6]" : "border-[#e6e8e7]",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left"
      >
        <span className="text-[13px] font-semibold text-[#051912]">{section.title}</span>
        <ChevronDown className={cn("size-4 text-[#8a9a93] transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div className="flex flex-col gap-3 border-t border-[#f0f2f1] px-4 py-3">
          {section.internal ? (
            <p className="flex items-center gap-1.5 rounded-md bg-[#fff6ef] px-2.5 py-1.5 text-[12px] text-[#a84a12]">
              <ShieldAlert className="size-3.5 shrink-0" />
              For your team only. Do not share this with the lead.
            </p>
          ) : null}
          {compact.length ? (
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {compact.map((item, i) => (
                <ItemView key={i} item={item} lead={lead} />
              ))}
            </div>
          ) : null}
          {rest.map((item, i) => (
            <ItemView key={i} item={item} lead={lead} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default function ToolRecordBody({ record, lead, surface = "tab" }: {
  record: Pick<ToolRecordView, "tab"> & Partial<Pick<ToolRecordView, "overview">>;
  lead: LeadContact;
  surface?: "tab" | "overview";
}) {
  const sections = record[surface] ?? [];
  if (sections.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {sections.map((section) => (
        <Section key={section.title} section={section} lead={lead} />
      ))}
    </div>
  );
}
